import { useState } from 'react';
import Alert from 'react-bootstrap/Alert';
import Button from 'react-bootstrap/Button';
import Form from 'react-bootstrap/Form';
import Table from 'react-bootstrap/Table';
import { toast } from 'react-toastify';
import authService from '../api-authorization/AuthorizeService';

const maximumCsvFileSize = 1024 * 1024;

const ExcludeFromAnalysis = () => {
    const [file, setFile] = useState(null);
    const [isUploading, setIsUploading] = useState(false);
    const [result, setResult] = useState(null);

    const handleFileChange = (event) => {
        setFile(event.target.files[0] ?? null);
        setResult(null);
    };

    const handleSubmit = async (event) => {
        event.preventDefault();

        if (!file) {
            toast.error('Select a CSV file to upload.');
            return;
        }

        if (!file.name.toLowerCase().endsWith('.csv')) {
            toast.error('Only CSV files can be uploaded.');
            return;
        }

        if (file.size > maximumCsvFileSize) {
            toast.error('The CSV file must not exceed 1 MB.');
            return;
        }

        setIsUploading(true);
        setResult(null);

        try {
            const token = await authService.getAccessToken();
            const formData = new FormData();
            formData.append('file', file);

            const response = await fetch('/api/admin/exclude-from-analysis', {
                method: 'POST',
                headers: !token ? { Accept: 'application/json' } : {
                    Accept: 'application/json',
                    Authorization: `Bearer ${token}`
                },
                body: formData
            });
            const responseBody = await response.json().catch(() => ({}));

            if (!response.ok) {
                throw new Error(responseBody.message || 'The CSV file could not be uploaded.');
            }

            setResult(responseBody);
            toast.success('The CSV file was processed successfully.');
        } catch (error) {
            toast.error(error.message || 'The CSV file could not be uploaded.');
        } finally {
            setIsUploading(false);
        }
    };

    return (
        <>
            <h3 className="mt-4">Exclude records from analysis</h3>
            <p>
                Upload a CSV containing the columns <code>Survey_Id</code>,{' '}
                <code>User_Email</code>, <code>Farm_Name</code> and <code>Year</code>.{' '}
                The maximum file size is 1 MB.
            </p>
            <Form onSubmit={handleSubmit}>
                <Form.Group className="mb-3" controlId="analysisExclusionCsv">
                    <Form.Label>CSV file</Form.Label>
                    <Form.Control
                        type="file"
                        accept=".csv,text/csv"
                        required
                        disabled={isUploading}
                        onChange={handleFileChange}
                    />
                </Form.Group>
                <Button type="submit" disabled={!file || isUploading}>
                    {isUploading ? 'Uploading...' : 'Upload CSV'}
                </Button>
            </Form>

            {result && (
                <Alert variant="success" className="mt-3">
                    <Alert.Heading>Import complete</Alert.Heading>
                    <ul className="mb-0">
                        <li>Rows read: {result.totalRows}</li>
                        <li>Rows processed: {result.processedRows}</li>
                        <li>Surveys excluded: {result.excludedSurveys}</li>
                        <li>Surveys already excluded: {result.alreadyExcludedSurveys}</li>
                        <li>Duplicate rows: {result.duplicateRows}</li>
                        <li>Rows not found: {result.notFoundRows}</li>
                    </ul>
                </Alert>
            )}

            {result?.issues?.length > 0 && (
                <Table striped bordered responsive className="mt-3">
                    <caption>Rows that could not be processed</caption>
                    <thead>
                        <tr>
                            <th scope="col">Row</th>
                            <th scope="col">Survey ID</th>
                            <th scope="col">User email</th>
                            <th scope="col">Farm name</th>
                            <th scope="col">Year</th>
                            <th scope="col">Issue</th>
                        </tr>
                    </thead>
                    <tbody>
                        {result.issues.map((issue) => (
                            <tr key={`${issue.rowNumber}-${issue.code}`}>
                                <td>{issue.rowNumber}</td>
                                <td>{issue.surveyId}</td>
                                <td>{issue.userEmail}</td>
                                <td>{issue.farmName}</td>
                                <td>{issue.year}</td>
                                <td>{issue.message}</td>
                            </tr>
                        ))}
                    </tbody>
                </Table>
            )}
        </>
    );
};

export default ExcludeFromAnalysis;
